/**
 * Demo form: 3-step wizard + AJAX submit.
 */

import {
  clearFieldErrors,
  showFieldErrors,
  submitAffilaeForm,
} from './contact-form';

function getSteps(form: HTMLFormElement): HTMLElement[] {
  return Array.from(form.querySelectorAll<HTMLElement>('.uacf7-step'));
}

function validateStep(form: HTMLFormElement, step: number): boolean {
  clearFieldErrors(form);
  const panel = getSteps(form)[step - 1];
  if (!panel) return true;

  const requiredMessage = form.dataset.msgRequired ?? '';
  const selectOneMessage = form.dataset.msgSelectOne ?? '';
  const rgpdMessage = form.dataset.msgRgpd ?? '';

  let valid = true;
  const required = panel.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    'input[required], select[required], textarea[required]',
  );

  required.forEach(el => {
    const filled =
      el instanceof HTMLInputElement && el.type === 'checkbox' ? el.checked : el.value.trim() !== '';
    if (filled) return;

    valid = false;
    showFieldErrors(form, { [el.name]: el.name === 'rgpd' ? rgpdMessage : requiredMessage });
  });

  if (step === getSteps(form).length) {
    const main = form.querySelectorAll<HTMLInputElement>('input[name="main-objectifs[]"]:checked');
    const help = form.querySelectorAll<HTMLInputElement>('input[name="aide-affilae[]"]:checked');

    if (main.length === 0) {
      valid = false;
      showFieldErrors(form, { 'main-objectifs': selectOneMessage });
    }
    if (help.length === 0) {
      valid = false;
      showFieldErrors(form, { 'aide-affilae': selectOneMessage });
    }
  }

  return valid;
}

function setStep(form: HTMLFormElement, step: number): void {
  getSteps(form).forEach((panel, index) => {
    const active = index === step - 1;
    panel.hidden = !active;
    panel.style.display = active ? '' : 'none';
  });

  form.querySelectorAll<HTMLElement>('.steps-step .btn-circle').forEach((circle, index) => {
    circle.classList.toggle('uacf7-btn-active', index === step - 1);
    circle.classList.toggle('uacf7-btn-default', index !== step - 1);
    circle.classList.toggle('completed', index < step - 1);
  });
}

function stepOfField(form: HTMLFormElement, name: string): number {
  const steps = getSteps(form);
  const index = steps.findIndex(panel => panel.querySelector(`[data-name="${name}"]`));

  return index === -1 ? 0 : index + 1;
}

function initDemoForm(): void {
  const form = document.querySelector<HTMLFormElement>('[data-affilae-form="demo"]');
  if (!form) return;

  const total = getSteps(form).length;
  let current = 1;
  setStep(form, current);

  const goTo = (step: number): void => {
    current = Math.min(total, Math.max(1, step));
    setStep(form, current);
  };

  form.querySelectorAll<HTMLButtonElement>('.uacf7-next').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!validateStep(form, current)) return;
      goTo(current + 1);
    });
  });

  form.querySelectorAll<HTMLButtonElement>('.uacf7-prev').forEach(btn => {
    btn.addEventListener('click', () => goTo(current - 1));
  });

  form.addEventListener('submit', (event: Event) => {
    event.preventDefault();

    // Validate every step; jump to the first invalid one (also stops an Enter
    // keypress in step 1/2 from submitting an incomplete form).
    for (let step = 1; step <= total; step += 1) {
      if (!validateStep(form, step)) {
        goTo(step);

        return;
      }
    }

    void submitAffilaeForm(form, errors => {
      const first = Object.keys(errors)
        .map(name => stepOfField(form, name))
        .filter(step => step > 0)
        .sort((a, b) => a - b)[0];

      if (first) goTo(first);
    });
  });
}

document.addEventListener('DOMContentLoaded', initDemoForm);
